import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  AppointmentResponse,
  AppointmentStatus,
  DayBoardResponse,
  WorklistSummaryResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { useChartsShown } from '../components/charts/useChartsShown';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/PageHeader';
import { useAuthStore } from '../auth/authStore';
import { useLocalisedName } from '../i18n/localisedName';
import { useBoardStream } from '../realtime/useBoardStream';
import { DayBoardOverview } from './insight/DayBoardOverview';
import { AppointmentDetails } from './scheduling/AppointmentDetails';
import { BookingModal } from './scheduling/BookingModal';
import { CompleteDialog } from './scheduling/CompleteDialog';
import { clinicDay, clinicTime, clinicToday, longDate, shiftDay } from './scheduling/clinicTime';
import { useBranchPicker } from './scheduling/useBranchPicker';

/** The forward moves reception makes, in the order the day makes them. */
const NEXT_STATUS: Readonly<Record<string, AppointmentStatus | null>> = {
  BOOKED: 'ARRIVED',
  CONFIRMED: 'ARRIVED',
  ARRIVED: 'IN_PROGRESS',
  IN_PROGRESS: 'COMPLETED',
  COMPLETED: null,
  CANCELLED: null,
  NO_SHOW: null,
};

/**
 * The branch day-board (FR-401) — the screen a receptionist has open all day.
 *
 * One column per doctor working, including the empty ones: an empty column is what tells the desk
 * there is capacity, and a board that only drew booked doctors would hide it.
 *
 * Live updates arrive over the board's socket and carry only "this changed"; the board refetches
 * itself. A visible indicator says whether that channel is up, because a screen that quietly
 * stopped updating twenty minutes ago is worse than one that admits it.
 *
 * Above the columns, the day as charts (CR-35): free and booked time, each doctor's day, the load
 * hour by hour and the rooms, worked out from this same board so they move with it. They fold away
 * for a desk that only wants the columns.
 */
export function DayBoardPage() {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const permissions = useAuthStore((state) => state.permissions);
  const canBook = permissions.has('APPOINTMENT_CREATE');
  const canUpdate = permissions.has('APPOINTMENT_UPDATE');
  const canCancel = permissions.has('APPOINTMENT_CANCEL');

  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const [on, setOn] = useState(clinicToday());
  const [booking, setBooking] = useState(false);
  const [cancelling, setCancelling] = useState<AppointmentResponse | null>(null);
  const [completing, setCompleting] = useState<AppointmentResponse | null>(null);
  const [opened, setOpened] = useState<AppointmentResponse | null>(null);
  const [chartsShown, showCharts] = useChartsShown('dayBoard');

  const live = useBoardStream(clinicId, branchId);

  const board = useApiQuery<DayBoardResponse>(
    queryKeys.dayBoard(clinicId ?? '', branchId ?? '', on),
    `/clinics/${clinicId}/appointments/board?branchId=${branchId}&on=${on}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  const worklists = useApiQuery<WorklistSummaryResponse[]>(
    queryKeys.worklists(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/worklists?branchId=${branchId}`,
    { enabled: Boolean(clinicId && branchId) && permissions.has('WORKLIST_READ') },
  );

  const boardKey = ['clinics', clinicId ?? '', 'appointments', 'board', branchId ?? ''] as const;

  const changeStatus = useApiMutation<
    AppointmentResponse,
    { readonly id: string; readonly status: AppointmentStatus }
  >(
    ({ id, status }) => ({
      path: `/clinics/${clinicId}/appointments/${id}/status`,
      options: { method: 'POST', body: { status } },
    }),
    { invalidates: [boardKey] },
  );

  const cancel = useApiMutation<
    AppointmentResponse,
    { readonly id: string; readonly reason: string }
  >(
    ({ id, reason }) => ({
      path: `/clinics/${clinicId}/appointments/${id}/cancel`,
      options: { method: 'POST', body: { reason } },
    }),
    { invalidates: [boardKey], onSuccess: () => setCancelling(null) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.dayBoard')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.dayBoard')}
        description={t('board.description')}
        actions={
          <>
            <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
              {chartsShown ? t('overview.hide') : t('overview.show')}
            </button>
            {canBook && branchId ? (
              <button type="button" className="button button--primary" onClick={() => setBooking(true)}>
                {t('board.book')}
              </button>
            ) : null}
          </>
        }
      />

      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {localised.name(branch)}
              </option>
            ))}
          </select>
        </label>

        {/* The date stepper is one control rather than four sitting beside each other: two arrows
            around the date they move, then Today. The arrows used to be typographic quotes set as
            button labels, which is a text glyph doing an icon's job and which rendered at a
            different weight in Arabic. Today is disabled when it is already today, so the control
            says where you are as well as offering to move you. */}
        <div className="field">
          <span className="field__label">{t('board.date')}</span>
          <div className="board__dates">
            <button
              type="button"
              className="button button--icon"
              aria-label={t('board.previousDay')}
              onClick={() => setOn(shiftDay(on, -1))}
            >
              <Icon name="chevronStart" size={20} />
            </button>
            <input
              type="date"
              value={on}
              onChange={(event) => setOn(event.target.value)}
              aria-label={t('board.date')}
              style={{ inlineSize: 'auto' }}
            />
            <button
              type="button"
              className="button button--icon"
              aria-label={t('board.nextDay')}
              onClick={() => setOn(shiftDay(on, 1))}
            >
              <Icon name="chevronEnd" size={20} />
            </button>
            <button
              type="button"
              className="button"
              disabled={on === clinicToday()}
              onClick={() => setOn(clinicToday())}
            >
              {t('board.today')}
            </button>
          </div>
        </div>

        {/* The socket's state, in the ink that matches what it means. A board that quietly stopped
            updating twenty minutes ago is worse than one that admits it, so "not live" is the
            state that gets the attention colour. */}
        <span
          className={live ? 'pill pill--live' : 'pill pill--waiting'}
          style={{ marginInlineStart: 'auto', alignSelf: 'center' }}
        >
          {live ? t('board.live') : t('board.notLive')}
        </span>
      </div>

      {(worklists.data ?? []).length > 0 ? (
        <div className="banner banner--warning" style={{ marginBlockEnd: 'var(--space-4)' }}>
          <Icon name="worklists" size={18} />
          {t('board.worklistBanner', {
            count: (worklists.data ?? []).reduce((total, list) => total + (list.pendingItems ?? 0), 0),
          })}
        </div>
      ) : null}

      {chartsShown && board.data && (board.data.columns ?? []).length > 0 ? (
        <DayBoardOverview board={board.data} isToday={on === clinicToday()} />
      ) : null}

      <AsyncSection
        query={board}
        skeletonRows={5}
        isEmpty={(data) => (data.columns ?? []).length === 0 && (data.unassigned ?? []).length === 0}
        emptyTitle={board.data?.branchClosed ? t('board.closedTitle') : t('board.emptyTitle')}
        emptyDescription={
          board.data?.branchClosed
            ? t('board.closedDescription', { date: longDate(on, i18n.language) })
            : t('board.emptyDescription')
        }
      >
        {(data) => (
          <div className="board">
            {(data.columns ?? []).map((column) => (
              <section
                className={column.absent ? 'board__column board__column--absent' : 'board__column'}
                key={column.employmentId}
              >
                <header className="board__column-header">
                  <div className="board__column-title">
                    <h2>{column.doctorName ?? t('board.unknownDoctor')}</h2>
                    {/* The column's load, where the eye already is. Without it the only way to
                        compare two doctors' days is to count cards. */}
                    <span className="board__count">{(column.appointments ?? []).length}</span>
                  </div>
                  {/* Every window of the day (CR-35), so a split shift reads as two and an hour of
                      time off shows as the gap it is. With the whole day off there are no windows,
                      and the hours they would have worked are shown, under the absence flag. */}
                  <p>
                    {(column.windows ?? []).length > 0
                      ? (column.windows ?? [])
                          .map((window) => `${clinicTime(window.from)} – ${clinicTime(window.until)}`)
                          .join(' · ')
                      : column.workingFrom
                        ? `${clinicTime(column.workingFrom)} – ${clinicTime(column.workingUntil)}`
                        : t('board.offRota')}
                  </p>
                  {/* FR-702: the column is still drawn — an absence cancels nobody — so the desk
                      has to be told, or a doctor who is not coming looks like one who is. The
                      whole column takes the blocked wash and this states why once, rather than
                      every stranded card inside it wearing the alarm. */}
                  {column.absent ? (
                    <span className="flag">
                      <Icon name="alert" size={14} />
                      {column.absenceReason
                        ? t('board.absentBecause', { reason: column.absenceReason })
                        : t('board.absent')}
                    </span>
                  ) : null}
                </header>

                {(column.appointments ?? []).length === 0 ? (
                  <p className="board__free">{t('board.nothingBooked')}</p>
                ) : (
                  <ul className="board__cards">
                    {(column.appointments ?? []).map((appointment) => (
                      <li key={appointment.id}>
                        <AppointmentCard
                          appointment={appointment}
                          canUpdate={canUpdate}
                          canCancel={canCancel}
                          onAdvance={(status) =>
                            // Finishing is a form, not a status (D-12, CR-27): it records the pulses
                            // and spends the package, which a status change never did.
                            status === 'COMPLETED'
                              ? setCompleting(appointment)
                              : changeStatus.mutate({ id: appointment.id ?? '', status })
                          }
                          onCancel={() => setCancelling(appointment)}
                          onDetails={() => setOpened(appointment)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}
      </AsyncSection>

      {booking && branchId ? (
        <BookingModal
          clinicId={clinicId}
          branchId={branchId}
          on={on}
          onClose={() => setBooking(false)}
          onBooked={(created) => {
            setBooking(false);
            if (created.startsAt) {
              setOn(clinicDay(created.startsAt));
            }
          }}
        />
      ) : null}

      {opened ? (
        <AppointmentDetails clinicId={clinicId} appointment={opened} onClose={() => setOpened(null)} />
      ) : null}

      {completing ? (
        <CompleteDialog
          clinicId={clinicId}
          appointment={completing}
          invalidates={[boardKey]}
          onClose={() => setCompleting(null)}
        />
      ) : null}

      {cancelling ? (
        <CancelDialog
          appointment={cancelling}
          pending={cancel.isPending}
          onClose={() => setCancelling(null)}
          onConfirm={(reason) => cancel.mutate({ id: cancelling.id ?? '', reason })}
        />
      ) : null}
    </>
  );
}

interface CardProps {
  readonly appointment: AppointmentResponse;
  readonly canUpdate: boolean;
  readonly canCancel: boolean;
  readonly onAdvance: (status: AppointmentStatus) => void;
  readonly onCancel: () => void;
  readonly onDetails: () => void;
}

function AppointmentCard({ appointment, canUpdate, canCancel, onAdvance, onCancel, onDetails }: CardProps) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const status = appointment.status ?? 'BOOKED';
  const next = NEXT_STATUS[status] ?? null;

  return (
    <article className={`card card--${status.toLowerCase()}`}>
      <header className="card__header">
        {/* The status is carried three ways: the dot's colour, the word in the badge, and the
            card's own treatment. It used to be a 3px rule down the card's inline-start edge,
            which is invisible at a glance on a white card in a column of white cards. */}
        <strong className="card__time">
          <span className="card__dot" aria-hidden="true" />
          {clinicTime(appointment.startsAt)}
        </strong>
        <span className={`badge badge--${statusTone(status)}`}>{t(`board.status.${status}`)}</span>
      </header>

      <p className="card__patient">
        {appointment.patientName}
        {(appointment.patientNoShowCount ?? 0) > 0 ? (
          // FR-404: a count, not a score. Staff read the number and decide.
          <span className="flag" title={t('board.noShowHint')}>
            {t('board.noShows', { count: appointment.patientNoShowCount ?? 0 })}
          </span>
        ) : null}
      </p>
      <p className="card__service">
        {localised.name({ nameEn: appointment.serviceNameEn, nameAr: appointment.serviceNameAr })}
        {appointment.areaNameEn || appointment.areaNameAr
          ? ` · ${localised.name({ nameEn: appointment.areaNameEn, nameAr: appointment.areaNameAr })}`
          : ''}
      </p>
      {appointment.roomName ? <p className="card__room">{appointment.roomName}</p> : null}
      {appointment.note ? (
        <p className="card__note card__note--flag">
          <Icon name="alert" size={14} />
          {appointment.note}
        </p>
      ) : null}
      {appointment.policyOverridden ? (
        <p className="card__note card__note--flag">
          <Icon name="alert" size={14} />
          {t('board.overridden', { reason: appointment.overrideReason ?? '' })}
        </p>
      ) : null}

      {/* One forward move, filled, and everything else one press further away.
          This card used to carry up to three equally weighted buttons, which on a normal day is
          twenty-five identical lozenges across the board and no way to see which one the desk was
          meant to press. "No-show" and "Cancel" are not the next step on any appointment — they
          are what you reach for when the next step does not happen — so they belong in an
          overflow, where they also stop being mis-tapped on a tablet. */}
      {/* The overflow is on every card, finished ones too, because Details is in it (CR-28): a
          completed or cancelled appointment is exactly the one somebody asks about afterwards. */}
      <footer className="card__actions">
        {canUpdate && next ? (
          <button type="button" className="button button--soft" onClick={() => onAdvance(next)}>
            {t(`board.advance.${next}`)}
          </button>
        ) : null}

        <details className="menu">
          <summary aria-label={t('board.moreActions', { patient: appointment.patientName ?? '' })}>
            <Icon name="more" size={20} />
          </summary>
          <div className="menu__items">
            <button type="button" className="menu__item" onClick={onDetails}>
              <Icon name="info" size={18} />
              {t('appointment.details')}
            </button>
            {canUpdate && (status === 'BOOKED' || status === 'CONFIRMED' || status === 'ARRIVED') ? (
              <button type="button" className="menu__item" onClick={() => onAdvance('NO_SHOW')}>
                <Icon name="close" size={18} />
                {t('board.advance.NO_SHOW')}
              </button>
            ) : null}
            {canCancel && !isFinished(status) ? (
              <button type="button" className="menu__item menu__item--danger" onClick={onCancel}>
                <Icon name="refunds" size={18} />
                {t('common.cancel')}
              </button>
            ) : null}
          </div>
        </details>
      </footer>
    </article>
  );
}

function CancelDialog({
  appointment,
  pending,
  onClose,
  onConfirm,
}: {
  readonly appointment: AppointmentResponse;
  readonly pending: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('board.cancelTitle')}>
      <div className="modal__panel modal__panel--narrow">
        <header className="modal__header">
          <h2>{t('board.cancelTitle')}</h2>
        </header>
        <div className="modal__body">
          <p>
            {t('board.cancelConfirm', {
              patient: appointment.patientName ?? '',
              time: clinicTime(appointment.startsAt),
            })}
          </p>
          {/* RULE-008: never refused, always classed. The desk is told which it will be after the
              fact, because the classification is the server's to make against the clinic's own
              window. */}
          <p className="field__hint">{t('board.cancelLateHint')}</p>
          <label className="field">
            <span className="field__label">{t('board.cancelReason')}</span>
            <input
              className="field__input"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
        </div>
        <footer className="modal__footer">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('common.back')}
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={pending}
            onClick={() => onConfirm(reason)}
          >
            {t('board.cancelAction')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function isFinished(status: string): boolean {
  return status === 'COMPLETED' || status === 'CANCELLED' || status === 'NO_SHOW';
}

function statusTone(status: string): string {
  switch (status) {
    case 'ARRIVED':
    case 'IN_PROGRESS':
      return 'gold';
    case 'COMPLETED':
      return 'success';
    case 'NO_SHOW':
      return 'danger';
    case 'CANCELLED':
      return 'muted';
    default:
      return 'muted';
  }
}
